/**
 * Refer to https://github.com/enzymejs/enzyme/issues/2176 for how to test React Contexts with a shallow
 * enzyme renderer
 */
import type { ReactNode } from "react";

import { createContext, useReducer, use } from "react";
import { BaseHighlightObject } from "../../models/HighlightObject";

const initialState: State = {
  boxes: [],
};
type HighlightProviderProps = {
  children: ReactNode;
};
type State = {
  boxes: BaseHighlightObject[];
};
type Action = {
  type: "add" | "remove" | "set";
  highlight: BaseHighlightObject | BaseHighlightObject[];
};
type Dispatch = (action: Action) => void;

const HighlightStateContext = createContext<State | undefined>(undefined);
HighlightStateContext.displayName = "HighlightStateContext";
const HighlightDispatchContext = createContext<Dispatch | undefined>(undefined);
HighlightDispatchContext.displayName = "HighlightDispatchContext";
// TODO: DRY
function highlightReducer(state: State, action: Action): State {
  const reducingHighlights = Array.isArray(action.highlight)
    ? action.highlight
    : [action.highlight];
  switch (action.type) {
    // add new highlights
    case "add": {
      return {
        boxes: [...state.boxes, ...reducingHighlights],
      };
    }
    // remove a highlight
    case "remove": {
      return {
        boxes: state.boxes.filter(
          (box) =>
            !reducingHighlights.find(
              (reducingBox) => reducingBox.id === box.id,
            ),
        ),
      };
    }
    // remove and add combined
    case "set": {
      return {
        boxes: reducingHighlights,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}
const HighlightProvider = (props: HighlightProviderProps) => {
  const [state, dispatch] = useReducer(highlightReducer, initialState);

  return (
    <HighlightStateContext value={state}>
      <HighlightDispatchContext value={dispatch}>
        {props.children}
      </HighlightDispatchContext>
    </HighlightStateContext>
  );
};
function useHighlightState() {
  const context = use(HighlightStateContext);
  if (context === undefined) {
    throw new Error(
      "useHighlightState must be used within a HighlightProvider",
    );
  }
  return context;
}
function useHighlightDispatch() {
  const context = use(HighlightDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useHighlightDispatch must be used within a HighlightProvider",
    );
  }
  return context;
}
/**
 * When needing to access the Highlight Provider from within a Class based component, use this
 */
function HighlightConsumer({ children }: any) {
  return (
    <HighlightStateContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error(
            "HighlightConsumer must be used within a HighlightProvider",
          );
        }
        return children(context);
      }}
    </HighlightStateContext.Consumer>
  );
}
/**
 * When needing to dispatch to Highlight Provider from a Class based component, use this
 */
function HighlightDispatcher({ children }: any) {
  return (
    <HighlightDispatchContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error(
            "HighlightDispatcher must be used within a HighlightProvider",
          );
        }
        return children(context);
      }}
    </HighlightDispatchContext.Consumer>
  );
}
export {
  HighlightProvider,
  useHighlightState,
  useHighlightDispatch,
  HighlightConsumer,
};
export { State as HighlightProviderState };
/**
 * Exported so RemoteImageViewer can access and update state
 */
export {
  HighlightStateContext,
  HighlightDispatchContext,
  Dispatch as HighlightProviderDispatch,
  HighlightDispatcher,
};
